<template>
  <Teleport to="body">
    <div class="power-dialer-banner" :class="bannerClass" :style="bannerStyle">
      <div class="power-dialer-banner__content">
        <Icon :icon="bannerIcon" class="power-dialer-banner__icon" />
        <div class="power-dialer-banner__text">
          <span class="power-dialer-banner__message">{{ statusMessage }}</span>
          <span v-if="summary" class="power-dialer-banner__stats">
            {{ summary.completed }}/{{ summary.total }} completed · {{ summary.pending }} pending
            <template v-if="summary.skipped">· {{ summary.skipped }} skipped</template>
          </span>
        </div>
      </div>
      <div class="power-dialer-banner__actions">
        <Button
          v-if="showOpenDialer"
          variant="secondary"
          size="sm"
          class="power-dialer-banner__ghost-btn"
          @click="$emit('open-dialer')"
        >
          Open Dialer
        </Button>
        <Button
          v-if="canTriggerNextCall"
          variant="secondary"
          size="sm"
          :loading="isRequestingNextCall"
          :disabled="isRequestingNextCall"
          class="power-dialer-banner__cta"
          @click="$emit('next-call')"
        >
          {{ hasStarted ? "Next Call" : "Start Session" }}
        </Button>
        <Button
          v-if="hasStarted && sessionStatus === 'paused'"
          variant="secondary"
          size="sm"
          :loading="isResuming"
          class="power-dialer-banner__cta"
          @click="$emit('resume')"
        >
          Resume
        </Button>
        <Button
          v-else-if="hasStarted"
          variant="secondary"
          size="sm"
          :disabled="isOnCall"
          :loading="isPausing"
          class="power-dialer-banner__ghost-btn"
          @click="$emit('pause')"
        >
          Pause
        </Button>
        <Button
          variant="secondary"
          size="sm"
          :loading="isEnding"
          class="power-dialer-banner__ghost-btn power-dialer-banner__ghost-btn--danger"
          @click="$emit('end')"
        >
          End Session
        </Button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import { useSidenavCollapse } from "@/composables/ui/useSidenavCollapse";
import { useGlobalFollowupModal } from "@/composables/bucket/useGlobalFollowupModal";

const props = defineProps({
  hasStarted: { type: Boolean, default: false },
  sessionStatus: { type: String, default: "active" },
  callStatus: { type: String, default: "ready" },
  contactName: { type: String, default: "" },
  callDurationFormatted: { type: String, default: "" },
  summary: { type: Object, default: null },
  canTriggerNextCall: { type: Boolean, default: false },
  awaitingDisposition: { type: Boolean, default: false },
  isRequestingNextCall: { type: Boolean, default: false },
  isPausing: { type: Boolean, default: false },
  isResuming: { type: Boolean, default: false },
  isEnding: { type: Boolean, default: false },
  // The banner shows on every page while a session exists — this links back
  // to the dialer queue page when the rep is somewhere else in the app.
  showOpenDialer: { type: Boolean, default: false },
  // Where the navbar area (navbar + any trial/billing/prompt banners under
  // it) ends, measured by DefaultLayout — so this sits below all of them
  // instead of on top of them. 64px is the bare navbar's height.
  topOffset: { type: Number, default: 64 },
});

defineEmits(["next-call", "pause", "resume", "end", "open-dialer"]);

const activeCallStates = ["connecting", "ringing", "in-call", "reconnecting"];

const isOnCall = computed(() => activeCallStates.includes(props.callStatus));

// Fixed + teleported to <body>, offset past the sidenav's actual width
// (matching the widths DefaultLayout itself uses — .custom-col-width-2 in
// index.css) so it never covers the logo/nav.
const { isCollapsed: isSidenavCollapsed, isDrawerMode: isSidenavDrawerMode } = useSidenavCollapse();

// Normally sits below the navbar area (see topOffset), like any other in-page
// banner. But TaskFollowupModal is a full-screen overlay with a
// higher z-index than the navbar — so while it's open, the banner instead
// pins to the very top (covering where the navbar would be, since the modal
// already does too) so it stays visible above the modal instead of being
// hidden behind it.
const { isModalOpen: isFollowupModalOpen } = useGlobalFollowupModal();

const bannerStyle = computed(() => ({
  left: isSidenavCollapsed.value || isSidenavDrawerMode.value ? "56px" : "260px",
  top: isFollowupModalOpen.value ? "0" : `${props.topOffset}px`,
}));

// Everything except the initial "not started yet" state reads as green now —
// the pause icon + "Power Dialer paused" label alone are enough to tell it
// apart from the active state; every distinct color tried for paused
// (orange, dark gray, purple, light gray) kept getting flagged, so settling
// on green everywhere avoids re-litigating this.
const bannerClass = computed(() => {
  if (!props.hasStarted && props.sessionStatus !== "paused") {
    return "power-dialer-banner--info";
  }
  return "power-dialer-banner--active";
});

const bannerIcon = computed(() => {
  if (isOnCall.value) return "mdi:phone-in-talk";
  if (props.awaitingDisposition) return "mdi:clipboard-text-clock-outline";
  if (props.sessionStatus === "paused") return "mdi:pause-circle";
  return "mdi:phone-outgoing";
});

const statusMessage = computed(() => {
  if (isOnCall.value) {
    const name = props.contactName || "contact";
    return props.callDurationFormatted
      ? `On call with ${name} — ${props.callDurationFormatted}`
      : `Calling ${name}…`;
  }
  if (props.awaitingDisposition) return "Call ended — save the disposition to continue";
  if (props.sessionStatus === "paused") return "Power Dialer paused";
  if (props.canTriggerNextCall) {
    return props.hasStarted ? "Ready for the next call" : "Power Dialer ready";
  }
  if (props.hasStarted) return "Power Dialer complete — all contacts dialed";
  return "Power Dialer";
});
</script>

<style scoped>
.power-dialer-banner {
  position: fixed;
  right: 0;
  z-index: 10100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 20px;
  min-height: 48px;
  color: #fff;
  transition:
    left 280ms cubic-bezier(0.16, 1, 0.3, 1),
    top 150ms ease;
}

.power-dialer-banner--info {
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
}

.power-dialer-banner--active {
  background: linear-gradient(90deg, #16a34a 0%, #22c55e 100%);
}

.power-dialer-banner__content {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.power-dialer-banner__icon {
  font-size: 22px;
  flex-shrink: 0;
  color: #fff;
}

.power-dialer-banner__text {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
}

.power-dialer-banner__message {
  font-size: 0.875rem;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
}

.power-dialer-banner__stats {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
}

.power-dialer-banner__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.power-dialer-banner__cta {
  background: #fff !important;
  border-color: #fff !important;
  color: #1f2937 !important;
  font-weight: 700 !important;
}

.power-dialer-banner__cta:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.9) !important;
}

.power-dialer-banner__ghost-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  color: #fff !important;
}

.power-dialer-banner__ghost-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.28) !important;
}

.power-dialer-banner__ghost-btn--danger {
  background: rgba(0, 0, 0, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.power-dialer-banner__ghost-btn--danger:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.32) !important;
}

@media (max-width: 640px) {
  .power-dialer-banner {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 12px;
  }

  .power-dialer-banner__text {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>
